/* global dataLayer */

import { graphql } from 'gatsby';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import qs from 'qs';
import c from './../utils/constants';
import Container from './../components/container';
import Header from './../components/header/header';
import SubHeader from './../components/header/sub-header';
import { navigate } from 'gatsby';
import * as utilFormat from './../utils/format';
import * as utilAwards from './../utils/awards';
import { Helmet } from 'react-helmet';
import Layout from '../components/layout';

export default class AwardTypeTemplate extends Component {
  constructor(props) {
    super(props);
    this.state = {
      search: '',
      hasMounted: false
    };
  }

  static propTypes = {
    data: PropTypes.object,
    location: PropTypes.object,
    pageContext: PropTypes.shape({
      awardType: PropTypes.string.isRequired,
      year: PropTypes.string.isRequired
    })
  };

  componentDidMount() {
    const {
      pageContext: { awardType, year },
      location: { search, pathname }
    } = this.props;

    this.setState({ hasMounted: true });

    const timePeriod = qs.parse(search, { ignoreQueryPrefix: true }).time_period;
    const pageTitle = this.getPageTitleAndMetaDescription(awardType, timePeriod, year).title;

    // send event to GTM
    typeof dataLayer !== 'undefined' &&
      dataLayer.push({
        pageTitle,
        virtualUrl: `${pathname}${search}`,
        event: 'virtualPageview'
      });
  }

  handleSearchChange = (search) => {
    this.setState({
      search
    });
  };

  getSelectedDatepickerOption = () => {
    const {
      location: { search },
      pageContext: { year }
    } = this.props;

    const timePeriod = qs.parse(search, { ignoreQueryPrefix: true }).time_period;
    return utilFormat.getDatepickerOption(year, timePeriod);
  };

  onDatepickerSelect = (selectedValue) => {
    const {
      pageContext: { awardType }
    } = this.props;

    const { year, quarterRange } = utilFormat.parseDatepickerOption(selectedValue);
    const route = utilFormat.getAwardTypeRoute(awardType, year, quarterRange);
    navigate(route);
  };

  getPageTitleAndMetaDescription = (awardType, timePeriod, year) => {
    if (timePeriod) {
      // Title: [Award Type] – [Qx–Qx] [Year] Speedtest Awards
      // Meta: [Award Type] Speedtest Awards won in [Qx–Qx] [Year]
      return {
        title: `${c.awardTypes[awardType].label} – ${timePeriod.toUpperCase()} ${year} Speedtest Awards`,
        desc: `${c.awardTypes[awardType].label} Speedtest Awards won in ${timePeriod.toUpperCase()} ${year}`
      };
    }

    // Title: [Award Type] Speedtest Awards – [Year]
    // Meta: A full list of the [Award Type] Speedtest Awards won in [Year]
    return {
      title: `${c.awardTypes[awardType].label} Speedtest Awards – ${year}`,
      desc: `A full list of the Speedtest Awards won in ${year}`
    };
  };

  generateEdgesAndDatePicker = (data) => {
    const edges = [];
    const datePicker = [];

    Object.keys(data).forEach((key) => {
      if (key.indexOf('allYear') > -1) {data[key].edges.map((edge) => (edge.node.winner ? edges.push(edge) : null));}

      if (key.indexOf('datePicker') > -1) {
        datePicker.push({
          year: parseInt(key.split('datePicker')[1]),
          edges: data[key].edges.filter((edge) => edge.node.timePeriod)
        });
      }
    });
    return { edges, datePicker };
  };

  render() {
    const {
      pageContext: { awardType, year },
      location: { search }
    } = this.props;

    const edgesAndDatePicker = this.generateEdgesAndDatePicker(this.props.data);

    const timePeriod = qs.parse(search, { ignoreQueryPrefix: true }).time_period;
    const timePeriodPlusYear = `${timePeriod || ''} ${year}`;
    const title = c.awardTypes[awardType].label;

    const headerData = this.getPageTitleAndMetaDescription(awardType, timePeriod, year);

    const rawAwards = utilAwards.convertEdgesToAwards(edgesAndDatePicker.edges);
    const filteredAwards = utilAwards.filterAwards(rawAwards, { awardType, timePeriod });

    const displayProp = 'locationName';
    const filteredAndSortedAwards = utilAwards.sortAlphabetically(filteredAwards, displayProp);

    const groupBy = timePeriod ? 'type' : 'timePeriod';
    const sectionOrder = groupBy === 'timePeriod' ? c.timePeriods : 'auto';
    const sections = utilAwards.groupAwards(filteredAndSortedAwards, groupBy, sectionOrder);
    const navItems = utilAwards.convertSectionsToNavItems(sections, groupBy, displayProp);

    const methodologyLink = utilAwards.computeMethodologyLink(filteredAndSortedAwards, c.methodologyLink);
    if (!this.state.hasMounted) { return null; }
    return (
      <Layout>
        <div>
          <Helmet>
            <html lang="en" />
            <meta name="description" content={headerData.desc} />
            <title>{headerData.title}</title>
          </Helmet>
          <div className="section section-dark section-blue section-md">
            <div className="container">
              <Header
                onSearchChange={this.handleSearchChange}
                searchData={utilFormat.edgesToSearchData(this.props.data.search.edges)}
                search={this.state.search}
              />
              <SubHeader title={title} timePeriod={timePeriodPlusYear} />
              <Container
                handleDatepickerSelect={this.onDatepickerSelect}
                year={year}
                datepickerData={edgesAndDatePicker.datePicker}
                template="type"
                sections={sections}
                groupedBy={groupBy}
                navItems={navItems}
                awardType
                timePeriod={timePeriod}
                methodologyLink={methodologyLink}
                datepickerSelectedOption={this.getSelectedDatepickerOption()}
              />
            </div>
          </div>
        </div>
      </Layout>
    );
  }
}

export const query = graphql`
  query AwardTypeQuery($awardTypeSlug: String!, $datePickerFilter: String!) {
    search: allSitePage {
      ...Search
    }
    datePicker2017: allYear2017Json(filter: { type: { eq: $datePickerFilter } }) {
      edges {
        node {
          timePeriod
        }
      }
    }
    datePicker2018: allYear2018Json(filter: { type: { eq: $datePickerFilter } }) {
      edges {
        node {
          timePeriod
        }
      }
    }
    datePicker2019: allYear2019Json(filter: { type: { eq: $datePickerFilter } }) {
      edges {
        node {
          timePeriod
        }
      }
    }
    datePicker2020: allYear2020Json(filter: { type: { eq: $datePickerFilter } }) {
      edges {
        node {
          timePeriod
        }
      }
    }
    datePicker2021: allYear2021Json(filter: { type: { eq: $datePickerFilter } }) {
      edges {
        node {
          timePeriod
        }
      }
    }
    datePicker2022: allYear2022Json(filter: { type: { eq: $datePickerFilter } }) {
      edges {
        node {
          timePeriod
        }
      }
    }
    datePicker2023: allYear2023Json(filter: { type: { eq: $datePickerFilter } }) {
      edges {
        node {
          timePeriod
        }
      }
    }
    datePicker2024: allYear2024Json(filter: { type: { eq: $datePickerFilter } }) {
      edges {
        node {
          timePeriod
        }
      }
    }
    datePicker2025: allYear2025Json(filter: { type: { eq: $datePickerFilter } }) {
      edges {
        node {
          timePeriod
        }
      }
    }
    datePicker2026: allYear2026Json(filter: { type: { eq: $datePickerFilter } }) {
      edges {
        node {
          timePeriod
        }
      }
    }
    allYear2017Json(filter: { fields: { awardTypeSlug: { eq: $awardTypeSlug } } }) {
      edges {
        node {
          locationName
          year
          type
          winner
          timePeriod
          type
          report {
            link
            text
          }
          providerCopy
          data {
            metric
            rankings {
              country_id
              type
              isp_id
              isp_name
              best_name
              speed_score
              mobile_carrier_id
              carrier_name
              download_speed
              upload_speed
              rating
            }
          }
        }
      }
    }
    allYear2018Json(filter: { fields: { awardTypeSlug: { eq: $awardTypeSlug } } }) {
      edges {
        node {
          locationName
          year
          type
          winner
          timePeriod
          type
          report {
            link
            text
          }
          report2 {
            link
            text
          }
          report3 {
            link
            text
          }
          providerCopy
          data {
            metric
            rankings {
              country_id
              type
              isp_id
              isp_name
              best_name
              speed_score
              mobile_carrier_id
              carrier_name
              download_speed
              upload_speed
              coverage_score
              coverage_name
              rating
            }
          }
        }
      }
    }
    allYear2019Json(filter: { fields: { awardTypeSlug: { eq: $awardTypeSlug } } }) {
      edges {
        node {
          locationName
          year
          type
          winner
          timePeriod
          type
          report {
            link
            text
          }
          report2 {
            link
            text
          }
          report3 {
            link
            text
          }
          providerCopy
          data {
            metric
            rankings {
              country_id
              type
              isp_id
              isp_name
              best_name
              speed_score
              mobile_carrier_id
              carrier_name
              download_speed
              upload_speed
              coverage_score
              coverage_name
              rating
            }
          }
        }
      }
    }
    allYear2020Json(filter: { fields: { awardTypeSlug: { eq: $awardTypeSlug } } }) {
      edges {
        node {
          locationName
          year
          type
          winner
          timePeriod
          type
          report {
            link
            text
          }
          report2 {
            link
            text
          }
          report3 {
            link
            text
          }
          providerCopy
          data {
            metric
            rankings {
              country_id
              type
              isp_id
              isp_name
              best_name
              speed_score
              mobile_carrier_id
              carrier_name
              top_rated_name
              download_speed
              upload_speed
              coverage_score
              coverage_name
              rating
            }
          }
        }
      }
    }
    allYear2021Json(filter: { fields: { awardTypeSlug: { eq: $awardTypeSlug } } }) {
      edges {
        node {
          locationName
          year
          type
          winner
          timePeriod
          type
          report {
            link
            text
          }
          report2 {
            link
            text
          }
          report3 {
            link
            text
          }
          providerCopy
          data {
            metric
            rankings {
              country_id
              type
              isp_id
              isp_name
              best_name
              speed_score
              mobile_carrier_id
              carrier_name
              top_rated_name
              download_speed
              upload_speed
              coverage_score
              coverage_name
              video_score
              video_name
              rating
            }
          }
        }
      }
    }
    allYear2020Json(filter: { fields: { awardTypeSlug: { eq: $awardTypeSlug } } }) {
      edges {
        node {
          locationName
          year
          type
          winner
          timePeriod
          type
          report {
            link
            text
          }
          report2 {
            link
            text
          }
          report3 {
            link
            text
          }
          providerCopy
          data {
            metric
            rankings {
              country_id
              type
              isp_id
              isp_name
              best_name
              speed_score
              mobile_carrier_id
              carrier_name
              top_rated_name
              download_speed
              upload_speed
              coverage_score
              coverage_name
              rating
            }
          }
        }
      }
    }
    allYear2022Json(filter: { fields: { awardTypeSlug: { eq: $awardTypeSlug } } }) {
      edges {
        node {
          locationName
          year
          type
          winner
          timePeriod
          type
          report {
            link
            text
          }
          report2 {
            link
            text
          }
          report3 {
            link
            text
          }
          providerCopy
          data {
            metric
            rankings {
              country_id
              type
              isp_id
              isp_name
              best_name
              speed_score
              mobile_carrier_id
              carrier_name
              top_rated_name
              download_speed
              upload_speed
              coverage_score
              coverage_name
              video_score
              game_score
              video_name
              rating
            }
          }
        }
      }
    }
    allYear2023Json(filter: { fields: { awardTypeSlug: { eq: $awardTypeSlug } } }) {
      edges {
        node {
          locationName
          year
          type
          winner
          timePeriod
          type
          report {
            link
            text
          }
          report2 {
            link
            text
          }
          report3 {
            link
            text
          }
          providerCopy
          data {
            metric
            rankings {
              country_id
              type
              isp_id
              isp_name
              best_name
              speed_score
              mobile_carrier_id
              carrier_name
              top_rated_name
              download_speed
              upload_speed
              coverage_score
              coverage_name
              video_score
              game_score
              best_5g_gaming_experience_name
              best_mobile_gaming_experience_name
              best_isp_gaming_experience_name
              video_name
              rating
            }
          }
        }
      }
    }
    allYear2024Json(filter: { fields: { awardTypeSlug: { eq: $awardTypeSlug } } }) {
      edges {
        node {
          locationName
          year
          type
          winner
          timePeriod
          type
          report {
            link
            text
          }
          report2 {
            link
            text
          }
          report3 {
            link
            text
          }
          providerCopy
          data {
            metric
            rankings {
              country_id
              type
              isp_id
              isp_name
              best_name
              speed_score
              mobile_carrier_id
              carrier_name
              top_rated_name
              download_speed
              upload_speed
              coverage_score
              coverage_name
              video_score
              game_score
              best_5g_gaming_experience_name
              best_mobile_gaming_experience_name
              best_isp_gaming_experience_name
              video_name
              rating
              overall_connectivity_score
              best_fixed_name
            }
          }
        }
      }
    }
    allYear2025Json(filter: { fields: { awardTypeSlug: { eq: $awardTypeSlug } } }) {
      edges {
        node {
          locationName
          year
          type
          winner
          timePeriod
          type
          report {
            link
            text
          }
          report2 {
            link
            text
          }
          report3 {
            link
            text
          }
          providerCopy
          data {
            metric
            rankings {
              country_id
              type
              isp_id
              isp_name
              best_name
              speed_score
              mobile_carrier_id
              carrier_name
              top_rated_name
              download_speed
              upload_speed
              coverage_score
              coverage_name
              video_score
              game_score
              best_5g_gaming_experience_name
              best_mobile_gaming_experience_name
              best_isp_gaming_experience_name
              video_name
              rating
              overall_connectivity_score
              best_fixed_name
              best_mobile_name
              best_5g_name
            }
          }
        }
      }
    }
    allYear2026Json(filter: { fields: { awardTypeSlug: { eq: $awardTypeSlug } } }) {
      edges {
        node {
          locationName
          year
          type
          winner
          timePeriod
          type
          report {
            link
            text
          }
          report2 {
            link
            text
          }
          report3 {
            link
            text
          }
          providerCopy
          data {
            metric
            rankings {
              country_id
              type
              isp_id
              isp_name
              best_name
              speed_score
              mobile_carrier_id
              carrier_name
              top_rated_name
              download_speed
              upload_speed
              coverage_score
              coverage_name
              video_score
              game_score
              best_5g_gaming_experience_name
              best_mobile_gaming_experience_name
              best_isp_gaming_experience_name
              video_name
              rating
              overall_connectivity_score
              best_fixed_name
              best_5g_name
            }
          }
        }
      }
    }
  }
`;
